import {
    Add,
    CenterSquare,
    OpenPanelFilledBottom,
    OpenPanelFilledLeft,
    OpenPanelFilledRight,
    OpenPanelFilledTop,
    TrashCan,
} from '@carbon/icons-react';
import { displayTextOf } from '@repo/shared/orpc/multi-language';
import { type ColorTokens, useTheme } from '@repo/theme';
import { HorizBox, Pad, PadBox, Separator } from 'component/basics';
import { ActionButton, TextButton } from 'component/button';
import { FormField } from 'component/form';
import { Checkbox } from 'component/form/checkbox';
import { ChapterSelectionModal } from 'component/modal/videovoting/chapter-selection-modal';
import { QuestionSelectorModal } from 'component/modal/videovoting/question-selector-modal';
import { VotingSelectionModal } from 'component/modal/videovoting/voting-selection-modal';
import { NavigationHeaderAction } from 'component/navigation/navigation-header';
import { addNotificationToast } from 'component/notification-toast';
import { DropDownSelector } from 'component/popup';
import { Heading, TextField, UtilityText } from 'component/text';
import { useSiloLanguage } from 'component/translation';
import { AccordionCard } from 'component/ui/accordion-card';
import { useAllSpaces, useSpace } from 'data/spaces';
import { createVideoVotingAsync, editVideoVotingAsync } from 'data/video-votings';
import type {
    VideoInformation,
    VideoVoting,
    VideoVotingChapter,
    VideoVotingQuestion,
    VideoVotingQuestionEstimation,
    VideoVotingQuestionEstimationPredeterminedAnswer,
    VideoVotingQuestionPoll,
    VideoVotingQuestionPollOption,
} from 'open-modules/zdf/component/videovoting/videovoting-types';
import { useAllVideoVotings } from 'open-modules/zdf/component/videovoting/videovoting-util';
import { auctionVideoVotingSlider, endometriosisVideoVotingEN } from 'open-modules/zdf/feature/videovoting/examples/video-voting-demo-data';
import { useState } from 'react';
import { View } from 'react-native';
import { useIsAdmin } from 'util/admin';
import { useAppData } from 'util/appdataContext';
import { isDevelopment } from 'util/constants';
import { useDatastore } from 'util/datastore';
import { goBack } from 'util/navigate';
import { t } from 'util/stdtypes';
import { generateRandomKey } from 'util/util';

type EditableVideoVotingQuestion = Partial<VideoVotingQuestion> & { [key: string]: unknown };

type VideoVotingEditorProps = {
    voting?: VideoVoting;
};

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function VideoVotingEditor({ voting }: VideoVotingEditorProps) {
    const isoLocale = useSiloLanguage();
    const appData = useAppData();
    const isAdmin = useIsAdmin();
    const { colors } = useTheme();
    const [name, setName] = useState(voting?.name ?? undefined);
    const [questions, setQuestions] = useState<EditableVideoVotingQuestion[]>(voting?.questions ?? []);
    const [chapters, setChapters] = useState(voting?.chapters ?? []);
    const [showQuestionSelector, setShowQuestionSelector] = useState(false);
    const [showVotingSelector, setShowVotingSelector] = useState(false);
    const [selectedQuestionKey, setSelectedQuestionKey] = useState(voting?.linkedSpaceKey ?? undefined);
    const [videoInformation, setVideoInformation] = useState<Partial<VideoInformation>>(voting?.videoUrl ? { url: voting.videoUrl } : {});
    const selectedSpace = useSpace(selectedQuestionKey ?? null);
    const { spaces: allSpaces } = useAllSpaces();
    const allVotings = useAllVideoVotings();
    const datastore = useDatastore();

    const updateQuestion = (index: number, updatedQuestion: EditableVideoVotingQuestion) => {
        setQuestions((prev) => prev.map((q, i) => (i === index ? updatedQuestion : q)).filter((c) => c != null));
    };

    const updateChapter = (index: number, updatedChapter: VideoVotingChapter | null) => {
        setChapters((prev) => prev.map((c, i) => (i === index ? updatedChapter : c)).filter((c) => c != null));
    };

    const prefillVoting = (voting: VideoVoting) => {
        setName(voting.name ?? '');
        setQuestions(voting.questions ?? []);
        setChapters(voting.chapters ?? []);
    };

    // The contract refuses a voting with no linked space, so the editor stops
    // before the round trip rather than surfacing a validation error for
    // something the screen already knows.
    const saveVoting = async () => {
        if (!selectedQuestionKey) {
            addNotificationToast({
                appData: appData,
                type: 'error',
                label: t('video_voting.no_space_selected', 'Select a space for this voting before saving.'),
                dismissable: true,
            });
            return;
        }

        const input = {
            name: name ?? '',
            linkedSpaceKey: selectedQuestionKey,
            videoUrl: videoInformation.url ?? null,
            questions: questions as VideoVotingQuestion[],
            chapters,
        };

        // A failed write already raises its own toast through the API client's
        // error interceptor, so the catch only suppresses the success path.
        try {
            if (!voting) {
                await createVideoVotingAsync(datastore, input);
                addNotificationToast({
                    appData: appData,
                    type: 'success',
                    label: t('status.creation_successful', 'Creation successful'),
                    dismissable: true,
                });
                goBack();
            } else {
                await editVideoVotingAsync(datastore, { votingKey: voting.votingKey, ...input });
                addNotificationToast({
                    appData: appData,
                    type: 'success',
                    label: t('status.edit_successful', 'Edit successful'),
                    dismissable: true,
                });
            }
        } catch {
            // Reported by the interceptor; the editor stays open so the author
            // can fix what it refused.
        }
    };

    const cancelVoting = async () => {
        // No voting given -> New creation
        if (!voting) {
            goBack();
        } else {
            setName(voting.name);
            setQuestions(voting?.questions ?? []);
            setChapters(voting?.chapters ?? []);
        }
    };

    return (
        <View style={{ gap: 16 }}>
            <HorizBox spread>
                <TextButton
                    label={t('zdf_videovoting_editor.prefill_data', 'Prefill data from exisiting voting')}
                    onPress={() => setShowVotingSelector(true)}
                />
                {voting && isDevelopment && isAdmin && (
                    <TextButton
                        label={t('zdf_videovoting_editor.open_preview', 'Open preview (debug)')}
                        onPress={() => {
                            datastore.pushSubscreen('debug');
                        }}
                    />
                )}
            </HorizBox>
            <FormField label={t('zdf_videovoting_editor.voting_title', 'Voting title')} required>
                <TextField
                    multiline={false}
                    onChange={setName}
                    placeholderText='Enter a title for the voting'
                    testID='voting-title-input'
                    value={name}
                />
            </FormField>
            <Separator />
            <HorizBox center>
                <Heading label={t('zdf_videovoting_editor.selected_space', 'Selected space: ')} />
                <Heading
                    color={selectedSpace ? colors.colorEntryPointColorBlue : undefined}
                    text={selectedSpace ? displayTextOf(selectedSpace.name, isoLocale) : 'No space assigned'}
                />
                <ActionButton
                    icon={Add}
                    label={t('zdf_videovoting_editor.assign_space', 'Assign a space')}
                    onPress={() => {
                        setShowQuestionSelector(!showQuestionSelector);
                    }}
                />
            </HorizBox>
            <Separator />
            <Heading label={t('zdf_videovoting_editor.chapters', 'Chapters:')} />
            <PadBox horiz={16}>
                <View style={{ gap: 16 }}>
                    {chapters?.map((chapter, i) => (
                        <View key={chapter.key}>
                            <ChapterEditor onChange={(chapter) => updateChapter(i, chapter)} value={chapter} />
                        </View>
                    ))}
                    <ActionButton
                        icon={Add}
                        label={t('zdf_videovoting_editor.add_chapter', 'Add Chapter')}
                        onPress={() => setChapters([...chapters, { key: generateRandomKey(20), name: '' }])}
                    />
                </View>
            </PadBox>
            <Separator />

            <VideoInformationEditor
                onUpdate={(v) => {
                    setVideoInformation(v);
                }}
                videoInfo={videoInformation}
            />
            <Heading label={t('zdf_videovoting_editor.questions', 'Questions:')} />
            {questions.map((question, i) => (
                <View key={question.key}>
                    {/* FIXME: check for null */}
                    <QuestionEditor
                        chapters={chapters}
                        onUpdate={(updatedQuestion) => updateQuestion(i, updatedQuestion!)}
                        question={question}
                        testIDPrefix={`question-${i}`}
                    />
                </View>
            ))}
            <ActionButton
                icon={Add}
                label={t('zdf_videovoting_editor.add_question', 'Add Question')}
                onPress={() =>
                    setQuestions([
                        ...questions,
                        {
                            key: generateRandomKey(20),
                            text: undefined,
                            timestamp: undefined,
                            type: 'poll',
                            options: [{ key: generateRandomKey(20) }, { key: generateRandomKey(20) }],
                        } as Partial<VideoVotingQuestionPoll>,
                    ])
                }
            />
            <NavigationHeaderAction label={t('form.cancel_button', 'Cancel')} onPress={cancelVoting} secondary />
            <NavigationHeaderAction label={t('form.save_button', 'Save')} onPress={saveVoting} />
            {showQuestionSelector && (
                <QuestionSelectorModal
                    onClose={() => setShowQuestionSelector(false)}
                    onSelect={(question) => {
                        setSelectedQuestionKey(question.spaceKey);
                        setShowQuestionSelector(false);
                    }}
                    questions={allSpaces}
                />
            )}
            {showVotingSelector && (
                <VotingSelectionModal
                    onClose={() => setShowVotingSelector(false)}
                    onSelect={(voting) => {
                        prefillVoting(voting);
                        setShowVotingSelector(false);
                    }}
                    votings={[
                        ...allVotings.votings,
                        ...(isDevelopment && isAdmin
                            ? [
                                  {
                                      ...endometriosisVideoVotingEN,
                                      name: endometriosisVideoVotingEN.name + ' (Demo)',
                                  } as unknown as VideoVoting,
                                  {
                                      ...auctionVideoVotingSlider,
                                      name: auctionVideoVotingSlider.name + ' (Demo)',
                                  } as unknown as VideoVoting,
                              ]
                            : []),
                    ]}
                />
            )}
        </View>
    );
}

function getPositionIconProps(colors: ColorTokens) {
    return { size: 32, color: colors.colorTextSecondary };
}
function getPositionIconPropsSelected(colors: ColorTokens) {
    return { size: 32, color: colors.colorEntryPointColorBlue };
}

type QuestionEditorProps = {
    question: Partial<VideoVotingQuestion>;
    onUpdate: (question?: Partial<VideoVotingQuestion>) => void;
    chapters?: VideoVotingChapter[];
    // Namespaces the testIDs of every field below, so E2E can address one
    // question's inputs among several. Each sub-editor appends its own suffix.
    testIDPrefix: string;
};

/* eslint-disable-next-line max-lines-per-function */
function QuestionEditor({ question, onUpdate, chapters = [], testIDPrefix }: QuestionEditorProps) {
    const { colors } = useTheme();
    const [showChapterModal, setChapterModal] = useState(false);
    const assignedChapter = chapters.find((c) => c.key === question.chapterKey);

    const dropdownOptions = [
        { key: 'poll', label: t('zdf_videovoting.poll_type', 'Poll') },
        { key: 'slider', label: t('zdf_videovoting.slider_type', 'Slider') },
    ];
    const changeQuestionType = (type: string) => {
        if (type === 'poll') {
            onUpdate({ ...question, type: 'poll' });
        } else if (type === 'slider') {
            onUpdate({ ...question, type: 'slider' });
        }
    };

    return (
        <View style={{ backgroundColor: colors.colorSurfaceSecondary, padding: 16 }}>
            <AccordionCard
                title={
                    <View style={{ width: '100%', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Heading text={question.text ?? 'Add a question title'} />
                        <Pad />
                        <UtilityText text={question.timestamp?.toString()} />
                    </View>
                }
            >
                <View style={{ gap: 16 }}>
                    <DropDownSelector
                        label={t('zdf_videovoting_editor.question_type', 'Question type')}
                        onChange={(value) => {
                            changeQuestionType(value);
                        }}
                        options={dropdownOptions}
                        value={question.type}
                    />
                    <FormField label={t('zdf_videovoting_editor.question_name', 'Question Name')} required>
                        <TextField
                            multiline={false}
                            onChange={(value) => {
                                onUpdate({ ...question, text: value });
                            }}
                            placeholderText='Enter a name for the question'
                            testID={`${testIDPrefix}-name-input`}
                            value={question.text}
                        />
                    </FormField>
                    <Separator />
                    <HorizBox center gap={8}>
                        <Heading label={t('zdf_videovoting_editor.selected_chapter', 'Selected chapter: ')} />
                        <Heading
                            color={assignedChapter && colors.colorEntryPointColorBlue}
                            text={assignedChapter?.name ?? 'No chapter assigned'}
                        />
                        {!question.chapterKey && (
                            <ActionButton
                                icon={Add}
                                label={t('zdf_videovoting_editor.assign_chapter', 'Assign a chapter')}
                                onPress={() => {
                                    setChapterModal(!showChapterModal);
                                }}
                            />
                        )}
                        {question.chapterKey && (
                            <ActionButton
                                icon={TrashCan}
                                label={t('zdf_videovoting_editor.remove_chapter', 'Remove chapter')}
                                onPress={() => onUpdate({ ...question, chapterKey: undefined })}
                            />
                        )}
                    </HorizBox>
                    <QuestionPlacementEditor onUpdate={onUpdate} question={question} />
                    <Separator />
                    <QuestionTimingEditorProps onUpdate={onUpdate} question={question} testIDPrefix={testIDPrefix} />
                    <Separator />
                    {question.type === 'poll' && (
                        <PollOptionsEditor
                            onUpdate={onUpdate as (question: Partial<VideoVotingQuestionPoll>) => void}
                            question={question}
                            testIDPrefix={testIDPrefix}
                        />
                    )}
                    {question.type === 'slider' && (
                        <SliderOptionsEditor
                            onUpdate={onUpdate as (question: Partial<VideoVotingQuestionEstimation>) => void}
                            question={question}
                            testIDPrefix={testIDPrefix}
                        />
                    )}
                    <Separator />
                    <ActionButton icon={TrashCan} iconProps={{ color: colors.colorTextWarning }} onPress={() => onUpdate(undefined)} />
                </View>
            </AccordionCard>
            {showChapterModal && (
                <ChapterSelectionModal
                    chapters={chapters}
                    onClose={() => setChapterModal(false)}
                    onSelect={(chapter) => {
                        onUpdate({ ...question, chapterKey: chapter.key });
                        setChapterModal(false);
                    }}
                />
            )}
        </View>
    );
}

type QuestionPlacementEditorProps = {
    question: Partial<VideoVotingQuestion>;
    onUpdate: (question?: Partial<VideoVotingQuestion>) => void;
};

function QuestionPlacementEditor({ question, onUpdate }: QuestionPlacementEditorProps) {
    const { colors } = useTheme();
    const positionIconProps = getPositionIconProps(colors);
    const positionIconPropsSelected = getPositionIconPropsSelected(colors);
    return (
        <>
            <Separator />
            <HorizBox center>
                <UtilityText label={t('zdf_videovoting_editor.layout', 'Layout:')} />
                <ActionButton
                    icon={CenterSquare}
                    iconProps={!question.position ? positionIconPropsSelected : positionIconProps}
                    onPress={() => {
                        onUpdate({ ...question, position: undefined });
                    }}
                />
                <ActionButton
                    icon={OpenPanelFilledLeft}
                    iconProps={question.position?.left ? positionIconPropsSelected : positionIconProps}
                    onPress={() => {
                        onUpdate({ ...question, position: { left: '5%' } });
                    }}
                />
                <ActionButton
                    icon={OpenPanelFilledRight}
                    iconProps={question.position?.right ? positionIconPropsSelected : positionIconProps}
                    onPress={() => {
                        onUpdate({ ...question, position: { right: '5%' } });
                    }}
                />
                <ActionButton
                    icon={OpenPanelFilledTop}
                    iconProps={question.position?.top ? positionIconPropsSelected : positionIconProps}
                    onPress={() => {
                        onUpdate({ ...question, position: { top: '5%' } });
                    }}
                />
                <ActionButton
                    icon={OpenPanelFilledBottom}
                    iconProps={question.position?.bottom ? positionIconPropsSelected : positionIconProps}
                    onPress={() => {
                        onUpdate({ ...question, position: { bottom: '5%' } });
                    }}
                />
            </HorizBox>
        </>
    );
}

type QuestionTimingEditorProps = {
    question: Partial<VideoVotingQuestion>;
    onUpdate: (question?: Partial<VideoVotingQuestion>) => void;
    testIDPrefix: string;
};
function QuestionTimingEditorProps({ question, onUpdate, testIDPrefix }: QuestionTimingEditorProps) {
    return (
        <>
            <FormField label={t('zdf_videovoting_editor.timestamp', 'Timestamp')} required>
                <HorizBox center gap={8}>
                    <TextField
                        multiline={false}
                        onChange={(value) => {
                            const num = parseFloat(value);
                            if (!isNaN(num)) {
                                onUpdate({ ...question, timestamp: num });
                            } else {
                                onUpdate({ ...question, timestamp: undefined });
                            }
                        }}
                        placeholderText='Time in seconds'
                        testID={`${testIDPrefix}-timestamp-input`}
                        value={question.timestamp?.toString()}
                    />
                </HorizBox>
            </FormField>
            <HorizBox>
                <Checkbox
                    label={t('zdf_videovoting_editor.stops_video', 'Stops video')}
                    onChange={() => {
                        onUpdate({ ...question, stopsVideo: !question.stopsVideo });
                    }}
                    value={question.stopsVideo}
                />
            </HorizBox>
        </>
    );
}

// Type specific fields for poll questions
type PollOptionsEditorProps = {
    question: Partial<VideoVotingQuestionPoll>;
    onUpdate: (question: Partial<VideoVotingQuestionPoll>) => void;
    testIDPrefix: string;
};
function PollOptionsEditor({ question, onUpdate, testIDPrefix }: PollOptionsEditorProps) {
    const updateOptions = (index: number, value: VideoVotingQuestionPollOption | undefined) => {
        const updatedOptions = [...(question.options ?? [])];
        // FIXME: check for null
        updatedOptions[index] = value!;
        onUpdate({ ...question, options: updatedOptions.filter((o) => o != null) as VideoVotingQuestionPollOption[] });
    };

    return (
        <FormField label={t('zdf_videovoting_editor.options', 'Options')} required={true}>
            <View style={{ gap: 8, padding: 8 }}>
                {question.options !== undefined &&
                    question.options.map((option, i) => (
                        <OptionEditor
                            key={option.key}
                            onUpdate={(value) => updateOptions(i, value)}
                            option={option}
                            testID={`${testIDPrefix}-option-${i}-input`}
                        />
                    ))}
            </View>

            {question.options?.length !== undefined && question.options?.length < 6 && (
                <ActionButton
                    icon={Add}
                    label={t('zdf_videovoting_editor.add_option', 'Add Option')}
                    onPress={() => {
                        const newOptions = [...(question.options ?? []), { key: generateRandomKey(20) } as VideoVotingQuestionPollOption];
                        onUpdate({ ...question, options: newOptions });
                    }}
                />
            )}
        </FormField>
    );
}

type OptionEditorProps = {
    option: VideoVotingQuestionPollOption;
    onUpdate: (option?: VideoVotingQuestionPollOption) => void;
    testID: string;
};
function OptionEditor({ option, onUpdate, testID }: OptionEditorProps) {
    return (
        <HorizBox center gap={8}>
            <TextField
                multiline={false}
                onChange={(txt) => {
                    onUpdate({ ...option, text: txt });
                }}
                placeholderText='Enter option text'
                testID={testID}
                value={option?.text}
            />
            <View>
                <ActionButton icon={TrashCan} onPress={() => onUpdate(undefined)} />
            </View>
        </HorizBox>
    );
}

type SliderOptionsEditorProps = {
    question: Partial<VideoVotingQuestionEstimation>;
    onUpdate: (question: Partial<VideoVotingQuestionEstimation>) => void;
    testIDPrefix: string;
};

// Type specific fields for slider questions FIXME: This function is too large
// and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function SliderOptionsEditor({ question, onUpdate, testIDPrefix }: SliderOptionsEditorProps) {
    return (
        <FormField label={t('zdf_videovoting_editor.slider_options', 'Slider options')} required={true}>
            <View style={{ gap: 8 }}>
                <HorizBox gap={16}>
                    <FormField label={t('zdf_videovoting_editor.minimum_value', 'Minimum value')} required={true}>
                        <TextField
                            multiline={false}
                            onChange={(value) => onUpdate({ ...question, minValue: value ? Number(value) : undefined })}
                            placeholderText='Enter a lower range'
                            testID={`${testIDPrefix}-min-input`}
                            value={question.minValue?.toString()}
                        />
                    </FormField>
                    <FormField label={t('zdf_videovoting_editor.maximum_value', 'Maximum value')} required={true}>
                        <TextField
                            multiline={false}
                            onChange={(value) => onUpdate({ ...question, maxValue: value ? Number(value) : undefined })}
                            placeholderText='Enter an upper range'
                            testID={`${testIDPrefix}-max-input`}
                            value={question.maxValue?.toString()}
                        />
                    </FormField>
                </HorizBox>
                <HorizBox>
                    <FormField label={t('zdf_videovoting_editor.unit', 'Unit')}>
                        <TextField
                            multiline={false}
                            onChange={(value) => onUpdate({ ...question, unit: value })}
                            placeholderText='Enter a unit'
                            value={question.unit}
                        />
                    </FormField>
                </HorizBox>
                <FormField label={t('zdf_videovoting_editor.slider_answers', 'Slider answers')}>
                    <PadBox horiz={16}>
                        {(question.predeterminedAnswers ?? []).map((answer, i) => (
                            <View key={`answer-${answer.text}-${answer.value}`}>
                                <HorizBox center gap={16}>
                                    <FormField label={t('zdf_videovoting_editor.answer_label', 'Answer label')} required={true}>
                                        <TextField
                                            multiline={false}
                                            onChange={(value) => {
                                                const updatedAnswers = [...(question.predeterminedAnswers ?? [])];
                                                updatedAnswers[i] = { ...answer, text: value };
                                                onUpdate({ ...question, predeterminedAnswers: updatedAnswers });
                                            }}
                                            placeholderText='Enter an answer text'
                                            value={answer.text}
                                        />
                                    </FormField>
                                    <FormField label={t('zdf_videovoting_editor.answer_value', 'Answer value')} required={true}>
                                        <TextField
                                            multiline={false}
                                            onChange={(value) => {
                                                const numValue = Number(value);
                                                if (!isNaN(numValue)) {
                                                    const updatedAnswers = [...(question.predeterminedAnswers ?? [])];
                                                    updatedAnswers[i] = { ...answer, value: numValue };
                                                    onUpdate({ ...question, predeterminedAnswers: updatedAnswers });
                                                }
                                            }}
                                            placeholderText='Enter a value'
                                            value={answer.value?.toString()}
                                        />
                                    </FormField>
                                    <View>
                                        <ActionButton
                                            icon={TrashCan}
                                            onPress={() => {
                                                const updatedAnswers = question?.predeterminedAnswers?.filter((_, index) => index !== i);
                                                onUpdate({ ...question, predeterminedAnswers: updatedAnswers });
                                            }}
                                        />
                                    </View>
                                </HorizBox>
                            </View>
                        ))}
                    </PadBox>
                    <ActionButton
                        icon={Add}
                        label={t('zdf_videovoting_editor.add_predetermined_answer', 'Add a predetermined answer')}
                        onPress={() => {
                            const newAnswers = [
                                ...(question.predeterminedAnswers ?? []),
                                { key: generateRandomKey(20), text: undefined, value: undefined },
                            ];
                            onUpdate({
                                ...question,
                                predeterminedAnswers: newAnswers as VideoVotingQuestionEstimationPredeterminedAnswer[],
                            });
                        }}
                    />
                </FormField>
            </View>
        </FormField>
    );
}

type ChapterEditorProps = {
    value: VideoVotingChapter;
    onChange: (chapter: VideoVotingChapter | null) => void;
};
function ChapterEditor({ value, onChange }: ChapterEditorProps) {
    return (
        <View>
            <FormField label={t('zdf_videovoting_editor.name', 'Name')} required={true}>
                <HorizBox center gap={16}>
                    <TextField
                        multiline={false}
                        onChange={(txt) => {
                            onChange({ ...value, name: txt });
                        }}
                        placeholderText='Enter a chapter name'
                        value={value?.name}
                    />
                    <View style={{ flexGrow: 1 }}>
                        <ActionButton icon={TrashCan} onPress={() => onChange(null)} />
                    </View>
                </HorizBox>
            </FormField>
        </View>
    );
}

type VideoInformationEditorProps = {
    videoInfo: Partial<VideoInformation>;
    onUpdate: (videoInformation: Partial<VideoInformation>) => void;
};

function VideoInformationEditor({ videoInfo, onUpdate }: VideoInformationEditorProps) {
    return (
        <>
            <Heading label={t('zdf_videovoting_editor.video_info', 'Video Info:')} />
            <PadBox horiz={16}>
                <HorizBox>
                    <TextField
                        onChange={(t) => {
                            onUpdate({ url: t });
                        }}
                        placeholderText='Video URL'
                        value={videoInfo.url}
                    />
                </HorizBox>
            </PadBox>
            <Separator />
        </>
    );
}
